import React, { useState, useEffect } from "react";
import { X, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import MobileBottomNav from "../../components/feature/MobileBottomNav";

import verycard from "../../assets/images/rewardimg.png";
import mtnLogo from "../../assets/images/mtn.png";
import gloLogo from "../../assets/images/glo.png";
import airtelLogo from "../../assets/images/airtel1.png";
import ninemobileLogo from "../../assets/images/9mobile.png";

import {
  buyData,
  getCashbackBalance,
  getDataNetworks,
  getDataPlans,
  getMyWallet,
} from "../../api/apiServices";
import TopDataOffersSection from "../../components/TopDataOffersSection";
import BannerSlider from "../dashboard/components/BannerSlider";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const NETWORK_LOGOS = {
  MTN: mtnLogo,
  GLO: gloLogo,
  AIRTEL: airtelLogo,
  "9MOBILE": ninemobileLogo,
};

const NETWORK_PREFIXES = {
  MTN: ["0803","0806","0810","0813","0814","0816","0703","0704","0706","0903","0906","0913","0916"],
  GLO: ["0805","0807","0811","0815","0705","0905","0915"],
  AIRTEL: ["0802","0808","0812","0701","0708","0901","0902","0904","0907","0912"],
  "9MOBILE": ["0809","0817","0818","0908","0909"],
};

const PHONE_REGEX = /^0[789][01]\d{8}$/;

// Desired display order for validity categories
const CATEGORY_ORDER = ["Daily", "Weekly", "Monthly", "General"];

// ─────────────────────────────────────────────────────────────────────────────
// Pure helpers  (defined outside component for stable references)
// ─────────────────────────────────────────────────────────────────────────────

const detectNetworkFromPhone = (phone) => {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length < 4) return "";
  for (const [net, prefixes] of Object.entries(NETWORK_PREFIXES)) {
    if (prefixes.some((p) => cleaned.startsWith(p))) return net;
  }
  return "";
};

const getLogoKey = (netName = "") => {
  const u = netName.toUpperCase();
  if (u.includes("MTN")) return "MTN";
  if (u.includes("GLO")) return "GLO";
  if (u.includes("AIRTEL")) return "AIRTEL";
  return "9MOBILE";
};

/**
 * Classifies a plan into a validity bucket.
 * Prefers the provider's dedicated `validity` string (e.g. "30 days")
 * over the free-text label, and counts the actual number of days so a
 * "30days" plan isn't mis-bucketed into "Daily" by a naive substring
 * match on "day".
 */
const getValidityCategory = (validity = "", label = "") => {
  const n = (validity || label).toLowerCase().replace(/\s+/g, "");

  const dayMatch = n.match(/(\d+)day/);
  if (dayMatch) {
    const days = parseInt(dayMatch[1], 10);
    if (days >= 28) return "Monthly";
    if (days >= 7) return "Weekly";
    return "Daily";
  }

  if (n.includes("week")) return "Weekly";
  if (n.includes("month")) return "Monthly";
  return "General";
};


// ─────────────────────────────────────────────────────────────────────────────
// Reusable sub-components (defined before they are used)
// ─────────────────────────────────────────────────────────────────────────────

/** Simple label / value row for the plan detail modal. */
const DetailRow = ({ label, value, valueClass = "font-medium text-gray-900" }) => (
  <div className="flex justify-between items-center">
    <span className="text-gray-500 text-sm">{label}</span>
    <span className={`text-sm ${valueClass}`}>{value}</span>
  </div>
);

/** Animated toggle switch — shared by both modals. */
// const CashbackToggle = ({ value, onToggle, balance }) => (
//   <div className="flex justify-between items-center">
//     <div>
//       <p className="text-gray-800 font-medium text-sm">Use Cashback</p>
//       <p className="text-gray-500 text-xs mt-0.5">
//         Available: ₦{(balance ?? 0).toLocaleString()}
//       </p>
//     </div>
//     <button
//       onClick={onToggle}
//       className={`w-14 h-8 rounded-full px-1 flex items-center transition-colors duration-200 focus:outline-none ${
//         value ? "bg-green-500" : "bg-gray-300"
//       }`}
//     >
//       <div
//         className={`w-6 h-6 rounded-full bg-white shadow transition-transform duration-200 ${
//           value ? "translate-x-6" : "translate-x-0"
//         }`}
//       />
//     </button>
//   </div>
// );

/**
 * Standard phone-numpad with backspace.
 * Fixes the original bug where `0` was orphaned in a 3-col grid.
 *
 * Layout:
 *   1  2  3
 *   4  5  6
 *   7  8  9
 *   ⌫  0  (empty)
 */
const PinPad = ({ pin, setPin }) => {
  const rows = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["⌫", "0", ""],
  ];

  const handleKey = (key) => {
    if (key === "⌫") {
      setPin((p) => p.slice(0, -1));
    } else if (key !== "" && pin.length < 4) {
      setPin((p) => p + key);
    }
  };

  return (
    <div className="space-y-2 mb-6">
      {rows.map((row, ri) => (
        <div key={ri} className="grid grid-cols-3 gap-2">
          {row.map((key, ki) => {
            if (key === "")
              return <div key={ki} aria-hidden />;

            return (
              <button
                key={ki}
                onClick={() => handleKey(key)}
                className={`py-5 rounded-2xl font-bold transition-all active:scale-95 ${
                  key === "⌫"
                    ? "bg-gray-100 active:bg-gray-200 text-xl flex items-center justify-center"
                    : "text-2xl bg-gray-100 active:bg-gray-200"
                }`}
              >
                {key}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Modal: Plan Detail
// ─────────────────────────────────────────────────────────────────────────────

const PlanModal = ({
  show,
  plan,
  recipient,
  setRecipient,
  balance,
  balanceLoading,
  cashbackBalance,
  cashbackEnabled,
  hasCashback,
  setHasCashback,
  payableAmount,
  isInsufficient,
  loading,
  errorMessage,
  onClose,
  onProceed,
  navigate,
}) => {
  if (!show || !plan) return null;

  const cleanPhone = recipient.replace(/\D/g, "");
  const isPhoneValid = PHONE_REGEX.test(cleanPhone);
  const canProceed = isPhoneValid && !isInsufficient && !loading;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 flex items-end z-50 mb-16"
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 260 }}
          className="bg-white rounded-t-3xl p-6 w-full max-w-md mx-auto"
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <button onClick={onClose} aria-label="Close">
              <X size={24} className="text-red-500" />
            </button>
            <p className="text-2xl font-bold text-[#3866a3]">
              ₦{payableAmount?.toLocaleString()}
            </p>
          </div>

          <div className="space-y-4">
            <DetailRow label="Product" value="Data Bundle" />

            {/* Recipient field — editable if not yet valid */}
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-500 text-sm">Recipient</span>
                {!isPhoneValid && (
                  <span className="text-xs text-amber-600 font-medium">Required</span>
                )}
              </div>
              {isPhoneValid ? (
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm">{recipient}</span>
                  <button
                    onClick={() => setRecipient("")}
                    className="text-xs text-blue-500 underline"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <input
                  type="tel"
                  value={recipient}
                  onChange={(e) =>
                    setRecipient(e.target.value.replace(/\D/g, "").slice(0, 11))
                  }
                  placeholder="08012345678"
                  maxLength={11}
                  autoFocus
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-base focus:outline-none focus:border-[#3866A3]"
                />
              )}
            </div>

            <DetailRow label="Plan" value={plan.label || "N/A"} />
            <DetailRow label="Amount" value={`₦${plan.amount?.toLocaleString()}`} />
            {cashbackEnabled && (
              <DetailRow
                label="Cashback"
                value={
                  hasCashback
                    ? `Using ₦${(cashbackBalance ?? 0).toLocaleString()}`
                    : "Not used"
                }
                valueClass="font-medium text-green-600"
              />
            )}

            <div className="h-px bg-gray-200" />
            {/* <CashbackToggle
              value={hasCashback}
              onToggle={() => setHasCashback((p) => !p)}
              balance={cashbackBalance}
            /> */}

            {/* Wallet balance */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium text-sm">Wallet Balance</span>
                {balanceLoading ? (
                  <span className="text-sm text-gray-400">Loading…</span>
                ) : (
                  <span
                    className={`text-xl font-bold ${
                      isInsufficient ? "text-red-600" : "text-green-700"
                    }`}
                  >
                    ₦{Number(balance || 0).toLocaleString()}
                  </span>
                )}
              </div>

              {isInsufficient && (
                <div
                  onClick={() => navigate("/add-fund")}
                  className="mt-3 p-3 bg-red-50 rounded-lg text-red-700 cursor-pointer flex justify-between items-center"
                >
                  <span className="text-sm">Insufficient balance</span>
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <span>
                      Add ₦{Math.max(payableAmount - (balance ?? 0), 0).toLocaleString()}
                    </span>
                    <ArrowRight size={16} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {errorMessage && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm text-center">
              {errorMessage}
            </div>
          )}

          <button
            onClick={isInsufficient ? () => navigate("/add-fund") : onProceed}
            disabled={!canProceed && !isInsufficient}
            className={`w-full mt-5 py-3.5 rounded-full text-white font-semibold text-base transition-all ${
              isInsufficient
                ? "bg-red-500 hover:bg-red-600"
                : canProceed
                ? "bg-[#3866a3] hover:bg-[#2d5a8c] active:scale-[0.98]"
                : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            {isInsufficient ? "Add Funds" : "Proceed to Pay"}
          </button>

          {!isPhoneValid && (
            <p className="text-center text-xs text-amber-600 mt-2">
              Enter a valid 11-digit phone number to continue
            </p>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Modal: PIN Entry
// ─────────────────────────────────────────────────────────────────────────────

const PinModal = ({
  show,
  pin,
  setPin,
  loading,
  errorMessage,
  setErrorMessage,
  successMessage,
  onClose,
  onConfirm,
}) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="bg-white rounded-3xl p-6 w-full max-w-sm"
          style={{ marginBottom: "80px" }}
        >
          <h3 className="text-xl font-bold text-center mb-5">Enter Transaction PIN</h3>

          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm text-center">
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-2xl text-sm text-center">
              {successMessage}
            </div>
          )}

          {/* PIN dot indicators */}
          <div className="flex justify-center gap-4 mb-6">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`w-4 h-4 rounded-full transition-all duration-150 ${
                  i < pin.length ? "bg-[#3866a3] scale-110" : "bg-gray-200"
                }`}
              />
            ))}
          </div>

          {/* Numpad */}
          <PinPad
            pin={pin}
            setPin={(updater) => {
              setPin(updater);
              if (errorMessage) setErrorMessage("");
            }}
          />

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-4 border border-gray-300 rounded-full text-gray-700 font-medium hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading || pin.length !== 4}
              className={`flex-1 py-4 rounded-full text-white font-semibold transition-all ${
                pin.length === 4 && !loading
                  ? "bg-[#3866a3] hover:bg-[#2d5a8c] active:scale-[0.98]"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
            >
              {loading ? "Processing…" : "Confirm"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

/**
 * Normalise a plan from ANY source (API, top-offers) into a
 * single consistent shape so modals never see `undefined` fields.
 *
 * Handles both the older shape (plan_code/label/amount/network) and
 * the AirtimeNigeria-style shape (package_code/plan_summary/validity/
 * regular_price/agent_price/dealer_price/network_operator).
 */
const normalizePlan = (plan) => ({
  plan_code: plan.plan_code || plan.package_code || "",
  label: (plan.label || plan.data || plan.size || plan.plan_summary || "").replace(
    /\|/g,
    " • "
  ),
  amount: Number(
    plan.amount ??
      plan.price ??
      plan.regular_price ??
      plan.agent_price ??
      plan.dealer_price ??
      0
  ),
  network: (plan.network || plan.network_operator || "").toLowerCase(),
  validity: plan.validity || "",

  // was: plan.available || ""  ← broke on boolean false
  available: plan.available ?? "",

  // Admin-assigned group (e.g. "Streamers"), or null when uncategorized.
  category: plan.category && plan.category.id ? plan.category : null,
});

// ─────────────────────────────────────────────────────────────────────────────
// Main Screen
// ─────────────────────────────────────────────────────────────────────────────

const BundleDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Network / plan data ─────────────────────────────────────────────────────
  const [networks, setNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [groupedPlans, setGroupedPlans] = useState({});
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  // Admin-created plan groups (e.g. "Streamers"). Separate from the
  // validity tabs above (`categories` = Daily / Weekly / Monthly).
  const [allPlans, setAllPlans] = useState([]);
  const [planGroups, setPlanGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState("all");
  const [plansLoading, setPlansLoading] = useState(false);

  // ── User / wallet ───────────────────────────────────────────────────────────
  const [recipient, setRecipient] = useState("");
  const [balance, setBalance] = useState(null);
  const [cashbackBalance, setCashbackBalance] = useState(0);
  // Admin switch (Platform Settings) — cashback UI stays hidden until the
  // server confirms the program is on.
  const [cashbackEnabled, setCashbackEnabled] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(true);

  // ── Purchase flow ───────────────────────────────────────────────────────────
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [hasCashback, setHasCashback] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // ── Derived values ──────────────────────────────────────────────────────────
  const detectedNetwork = detectNetworkFromPhone(recipient);

  const payableAmount = hasCashback
    ? Math.max((selectedPlan?.amount ?? 0) - (cashbackBalance ?? 0), 0)
    : (selectedPlan?.amount ?? 0);

  // Fix: use payableAmount (cashback-aware) for the insufficient check
  const isInsufficient =
    selectedPlan !== null && balance !== null && payableAmount > balance;

  // ── API helpers ─────────────────────────────────────────────────────────────

  const fetchBalance = async () => {
    setBalanceLoading(true);
    try {
      const res = await getMyWallet();
      setBalance(res?.balance ?? 0);
    } catch {
      setBalance(0);
    } finally {
      setBalanceLoading(false);
    }
  };

  const fetchCashback = async () => {
    try {
      const res = await getCashbackBalance();
      setCashbackBalance(res?.cashbackBalance ?? 0);
      setCashbackEnabled(Boolean(res?.cashbackEnabled));
    } catch {}
  };

  const fetchNetworks = async () => {
    try {
      const res = await getDataNetworks();
      const list = res?.data?.networks || [];
      setNetworks(list);
      if (list.length > 0) setSelectedNetwork(list[0].identifier);
    } catch (err) {
      console.error("Failed to load networks", err);
    }
  };

  // Admin-created groups present in this network's plans, in the admin's
  // chosen order. Empty → the chip row isn't rendered at all.
  const collectPlanGroups = (plans) => {
    const byId = new Map();
    for (const plan of plans) {
      if (plan.category && !byId.has(plan.category.id)) {
        byId.set(plan.category.id, plan.category);
      }
    }
    return Array.from(byId.values()).sort(
      (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
    );
  };

  // Filters by the chosen admin group, then buckets by validity
  // (Daily / Weekly / Monthly) for the existing tabs.
  const applyValidityGrouping = (plans, groupId) => {
    const visible =
      groupId === "all" ? plans : plans.filter((p) => p.category?.id === groupId);

    const grouped = {};
    for (const plan of visible) {
      const cat = getValidityCategory(plan.validity, plan.label);
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(plan);
    }

    // Sort categories in preferred display order
    const cats = Object.keys(grouped).sort(
      (a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b)
    );

    setGroupedPlans(grouped);
    setCategories(cats);
    setActiveCategory(cats[0] || "");
  };

  const handleSelectGroup = (groupId) => {
    setActiveGroup(groupId);
    applyValidityGrouping(allPlans, groupId);
  };

  const fetchPlans = async (identifier) => {
  if (!identifier) return;
  setPlansLoading(true);
  setGroupedPlans({});
  setCategories([]);
  setAllPlans([]);
  setPlanGroups([]);
  setActiveGroup("all");

  try {
    const res = await getDataPlans(identifier);
    const raw =
      res?.data?.plans ||
      (Array.isArray(res?.data) ? res.data : null) ||
      res?.data?.data ||
      res?.data?.plans ||
      [];

    if (!raw.length) {
      setErrorMessage("No plans available for this network");
      return;
    }

    const seen = new Set();
    const plans = [];

    for (const p of raw) {
      const normalized = normalizePlan(p);

      if (normalized.available === false || normalized.available === "false" || normalized.available === 0) {
        continue;
      }

      const key = normalized.plan_code || normalized.label;
      if (key && !seen.has(key)) {
        seen.add(key);
        plans.push(normalized);
      }
    }

    if (!plans.length) {
      setErrorMessage("No active plans available for this network");
      return;
    }

    setAllPlans(plans);
    setPlanGroups(collectPlanGroups(plans));
    applyValidityGrouping(plans, "all");
  } catch (err) {
    console.error("Failed to load plans:", err);
    setErrorMessage("Failed to load data plans. Please try again.");
  } finally {
    setPlansLoading(false);
  }
};

  // ── Effects ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchBalance();
    fetchNetworks();
    fetchCashback();
  }, []);

  useEffect(() => {
    if (selectedNetwork) fetchPlans(selectedNetwork);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNetwork]);

  // Handle deep-link from top offers (runs once on mount)
  useEffect(() => {
    const state = location.state;
    if (state?.selectedOffer && state?.openPlanModal) {
      openPlanModal(state.selectedOffer);
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Actions ─────────────────────────────────────────────────────────────────

  /** Open the plan detail modal, clearing any stale messages. */
  const openPlanModal = (plan) => {
    setSelectedPlan(normalizePlan(plan));
    setErrorMessage("");
    setSuccessMessage("");
    setPin("");
    setShowPlanModal(true);
  };

  const handleSelectNetwork = (identifier) => {
    if (identifier === selectedNetwork) return;
    setSelectedNetwork(identifier);
    setSelectedPlan(null);
    setActiveCategory("");
  };

  const handleBuyData = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    const cleanPhone = recipient.replace(/\D/g, "");
    if (!cleanPhone || !PHONE_REGEX.test(cleanPhone)) {
      setErrorMessage("Please enter a valid 11-digit Nigerian phone number");
      return;
    }
    if (!selectedPlan) { setErrorMessage("Please select a data plan"); return; }
    if (!pin || pin.length !== 4) { setErrorMessage("Please enter your 4-digit PIN"); return; }
    if (!selectedNetwork) { setErrorMessage("Please select a network"); return; }

    setLoading(true);
    try {
      // Positional args — consistent with the mobile implementation
      await buyData(
        cleanPhone,
        selectedNetwork,
        selectedPlan.plan_code,
        selectedPlan.amount,
        pin,
        hasCashback
      );

      setSuccessMessage("Data bundle purchased successfully!");
      await fetchBalance();
      setShowPinModal(false);
      setShowPlanModal(false);
      setPin("");
      setSelectedPlan(null);
      setRecipient("");
      setHasCashback(false);
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Transaction failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="min-h-screen bg-gray-50 pb-24">
        {/* Header */}
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-10 shadow-sm">
          <button
            onClick={() => navigate(-1)}
            className="text-[17px] font-bold text-[#3866a3]"
          >
            ← Bundle
          </button>
          <button
            onClick={() => navigate("/history")}
            className="text-sm font-medium text-gray-600"
          >
            History
          </button>
        </div>

        <div className="px-4 pt-5 max-w-md mx-auto">
          <BannerSlider />
        </div>

        <div className="px-4 pt-5 space-y-8 max-w-md mx-auto">

          {/* ── Network Selector ── */}
          <div className="space-y-3">
            <label className="text-gray-600 text-sm font-medium block">Select Network</label>
            <div className="grid grid-cols-3 gap-3">
              {networks.map((net) => {
                const isActive = selectedNetwork === net.identifier;
                const logoKey = getLogoKey(net.name);
                return (
                  <div
                    key={net.identifier}
                    onClick={() => handleSelectNetwork(net.identifier)}
                    className={`flex flex-col items-center justify-center border-2 rounded-2xl py-4 px-2 cursor-pointer transition-all active:scale-95 ${
                      isActive
                        ? "border-[#3866A3] bg-blue-50 shadow-md"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <img
                      src={NETWORK_LOGOS[logoKey]}
                      alt={net.name}
                      className="w-12 h-12 object-contain mb-2"
                    />
                    <p
                      className={`text-center text-xs font-medium leading-tight ${
                        isActive ? "text-[#3866A3]" : "text-gray-700"
                      }`}
                    >
                      {net.name}
                    </p>
                  </div>
                );
              })}
            </div>
            {detectedNetwork && (
              <p className="text-xs text-blue-600 pl-1">
                Detected: <span className="font-medium">{detectedNetwork}</span>
              </p>
            )}
          </div>

          {/* ── Phone Number Input ── */}
          <div className="space-y-2">
            <label className="text-gray-600 text-sm font-medium">
              Recipient Phone Number
            </label>
            <div className="flex items-center border rounded-full px-3 py-2.5 bg-white shadow-sm focus-within:ring-2 focus-within:ring-[#3866a3]">
              {detectedNetwork && NETWORK_LOGOS[detectedNetwork] ? (
                <img
                  src={NETWORK_LOGOS[detectedNetwork]}
                  alt={detectedNetwork}
                  className="w-7 h-7 mr-2.5 rounded-full object-cover border"
                />
              ) : (
                <span className="w-7 h-7 mr-2.5 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500 shrink-0">
                  ?
                </span>
              )}
              <input
                type="tel"
                value={recipient}
                onChange={(e) =>
                  setRecipient(e.target.value.replace(/\D/g, "").slice(0, 11))
                }
                className="flex-1 bg-transparent outline-none text-base font-medium"
                placeholder="0803 XXX XXXX"
                maxLength={11}
              />
            </div>
          </div>

          {/* ── Top Data Offers ── */}
          <TopDataOffersSection
            title="Top Data Offers"
            maxItems={3}
            onOfferPress={openPlanModal}
            containerStyle="mt-2"
          />

          {/* ── All Plans ── */}
          <div className="bg-white rounded-2xl shadow-md p-5 space-y-5">
            <h2 className="text-lg font-semibold text-gray-800">All Data Plans</h2>

            {/* Admin-created plan groups (e.g. Streamers) */}
            {!plansLoading && planGroups.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                {[{ id: "all", name: "All Plans", icon: "" }, ...planGroups].map((group) => (
                  <button
                    key={group.id}
                    onClick={() => handleSelectGroup(group.id)}
                    className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-semibold transition-all ${
                      activeGroup === group.id
                        ? "bg-[#3866a3] border-[#3866a3] text-white"
                        : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
                    }`}
                  >
                    {group.icon ? <span>{group.icon}</span> : null}
                    {group.name}
                  </button>
                ))}
              </div>
            )}

            {plansLoading ? (
              <div className="text-center py-8 text-gray-500 animate-pulse">
                Loading plans…
              </div>
            ) : categories.length > 0 ? (
              <>
                {/* Category tabs */}
                <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
                        activeCategory === cat
                          ? "bg-white shadow text-[#3866a3]"
                          : "text-gray-600 hover:text-gray-800"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                {/* Plan grid — key on plan_code (not index) to prevent ghost selections */}
                <div className="grid grid-cols-3 gap-3">
                  {(groupedPlans[activeCategory] || []).map((plan) => (
                    <div
                      key={plan.plan_code}
                      onClick={() => openPlanModal(plan)}
                      className="border rounded-xl p-3 hover:bg-gray-50 cursor-pointer transition-all hover:shadow-sm active:scale-95"
                    >
                      <div className="text-sm font-semibold leading-tight line-clamp-2">
                        {plan.label}
                      </div>
                      <div className="text-base font-bold text-[#3866a3] mt-1">
                        ₦{plan.amount?.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                No plans available for this network yet
              </div>
            )}
          </div>

          {/* Global messages (only shown when no modal is open) */}
          {!showPlanModal && !showPinModal && successMessage && (
            <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-2xl text-center font-medium">
              ✅ {successMessage}
            </div>
          )}
          {!showPlanModal && !showPinModal && errorMessage && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-center font-medium">
              {errorMessage}
            </div>
          )}
        </div>
      </div>

      {/* ── Plan Detail Modal ── */}
      <PlanModal
        show={showPlanModal}
        plan={selectedPlan}
        recipient={recipient}
        setRecipient={setRecipient}
        balance={balance}
        balanceLoading={balanceLoading}
        cashbackBalance={cashbackBalance}
        cashbackEnabled={cashbackEnabled}
        hasCashback={hasCashback}
        setHasCashback={setHasCashback}
        payableAmount={payableAmount}
        isInsufficient={isInsufficient}
        loading={loading}
        errorMessage={errorMessage}
        onClose={() => {
          setShowPlanModal(false);
          setErrorMessage("");
        }}
        onProceed={() => {
          setErrorMessage("");
          setShowPinModal(true);
        }}
        navigate={navigate}
      />

      {/* ── PIN Modal ── */}
      <PinModal
        show={showPinModal}
        pin={pin}
        setPin={setPin}
        loading={loading}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        successMessage={successMessage}
        onClose={() => {
          setShowPinModal(false);
          setPin("");
          setErrorMessage("");
        }}
        onConfirm={handleBuyData}
      />

      <MobileBottomNav />
    </>
  );
};

export default BundleDashboard;
